import type { UseSoftphoneKeypadReturn } from './index.types';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { EventService } from 'Modules/softphone/services/EventService';
import type { ExtendedSession } from 'Modules/softphone/services/WebRTCService/index.types';
import { useSoftphoneStore, selectPermissions } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import { useCallback, useEffect, useRef } from 'react';

const logger = createLogger('InCallKeypad');

export const useSoftphoneKeypad = (): UseSoftphoneKeypadReturn => {
  const {
    canUseKeypad,
    keypadDigits,
    isKeypadActive,
    currentCallId,
    clearKeypadDigits,
    closeKeypad,
  } = useSoftphoneStore((state) => ({
    canUseKeypad: selectPermissions(state).canUseKeypad,
    keypadDigits: state.keypadDigits,
    isKeypadActive: state.isKeypadActive,
    currentCallId: state.currentCallId,
    clearKeypadDigits: state.clearKeypadDigits,
    closeKeypad: state.closeKeypad,
  }));

  /**
   * Handle digit press - send DTMF tone and update digits display
   */
  const handleDigitPress = useCallback(
    (digit: string) => {
      // Get current digits from store directly to avoid dependency
      const currentDigits = useSoftphoneStore.getState().keypadDigits;

      // Always update digits display first
      const newDigits = currentDigits + digit;
      useSoftphoneStore.getState().setKeypadDigits(newDigits);

      // Play DTMF tone locally (matches legacy behavior with 0.2 volume)
      const audioService: AudioService = AudioService.getInstance();
      audioService.playDTMFTone(digit);

      // Then try to send DTMF if we have an active call with session
      if (!currentCallId) {
        logger.warn('Cannot send DTMF: no active call');
        return;
      }

      const state = useSoftphoneStore.getState();
      const call = state.activeCalls.get(currentCallId);

      if (!call?.session) {
        logger.warn('Cannot send DTMF: no active session', {
          callId: currentCallId,
        });
        return;
      }

      // Send DTMF tone through SIP session
      const session = call.session as unknown as ExtendedSession;
      if (session.sessionDescriptionHandler?.sendDtmf) {
        try {
          session.sessionDescriptionHandler.sendDtmf(digit);
          logger.info('DTMF tone sent', { digit, callId: currentCallId });
          // The RTP channel: `sendDtmf` writes RFC 2833 telephone-events into
          // the media stream, so this never crosses the SIP socket.
          EventService.getInstance().logRtpEvent(
            'outgoing',
            `DTMF → ${digit}`,
            { callId: currentCallId, digit }
          );
        } catch (error) {
          logger.error('Failed to send DTMF tone', {
            digit,
            callId: currentCallId,
            error: (error as Error).message,
          });
          EventService.getInstance().logRtpEvent(
            'outgoing',
            `DTMF failed → ${digit}`,
            {
              callId: currentCallId,
              digit,
              error: (error as Error).message,
            }
          );
        }
      } else {
        logger.warn('Cannot send DTMF: sendDtmf not available', {
          callId: currentCallId,
        });
        EventService.getInstance().logRtpEvent(
          'outgoing',
          `DTMF unavailable → ${digit}`,
          { callId: currentCallId, digit, reason: 'sendDtmf not available' }
        );
      }
    },
    [currentCallId]
  );

  /**
   * Handle keypad close - close keypad and clear digits
   */
  const handleClose = useCallback(() => {
    closeKeypad();
    clearKeypadDigits();
  }, [closeKeypad, clearKeypadDigits]);

  /**
   * Call switch detection - close keypad when user switches to a different call
   * Uses a ref to track the previous call ID and closes keypad when it changes
   */
  const previousCallIdRef = useRef<string | null>(null);

  useEffect(() => {
    // If keypad just opened, store the current call ID
    if (isKeypadActive && previousCallIdRef.current === null) {
      previousCallIdRef.current = currentCallId;
      return;
    }

    // If keypad is open and call ID changed, close keypad
    if (isKeypadActive && previousCallIdRef.current !== null && previousCallIdRef.current !== currentCallId) {
      logger.info('Call switched, closing keypad', {
        from: previousCallIdRef.current,
        to: currentCallId,
      });
      handleClose();
      previousCallIdRef.current = currentCallId;
      return;
    }

    // Reset ref when keypad closes
    if (!isKeypadActive) {
      previousCallIdRef.current = null;
    }
  }, [isKeypadActive, currentCallId, handleClose]);

  return {
    keypadDigits,
    isKeypadActive,
    canUseKeypad,
    handleDigitPress,
    handleClose,
  };
};
